import { Fragment, useCallback, useMemo, useRef, useState } from 'react'

import { NextPage } from 'next'
import Head from 'next/head'
import Link from 'next/link'

import { cx } from '@linaria/core'
import { useErrorBoundary } from 'react-error-boundary'
import InfiniteScroll from 'react-infinite-scroll-component'
import { KeyedMutator, SWRResponse } from 'swr'

import { useFoodSWRInfinite } from '~/api-client/food_api'
import {
  useUserRecommendedUsers,
  userFollowUser,
  userUnfollowUser,
} from '~/api-client/food_api/user'
import { useUserRookieUsers } from '~/api-client/food_api/user/invited_user'
import { User } from '~/api-definition/definition'
import { UsersResponse } from '~/api-definition/response'
import { Button, ButtonGroup } from '~/components/action/Button'
import { AppImage } from '~/components/data-display/AppImage'
import { Heading } from '~/components/data-display/Heading'
import { TextField } from '~/components/data-input/TextField'
import { UserWarningStatusIconButton } from '~/components/food_app/user/UserWarningStatus'
import { SearchIcon } from '~/components/icons'
import { Container, ContainerInset } from '~/components/layout/Container'
import { Footer } from '~/components/layout/Footer'
import { Header } from '~/components/layout/Header'
import { config } from '~/config'
import { userAttributesLabel, userProfileIcon } from '~/libs/user'
import { useSession } from '~/provider/SessionProvider'

type UsersProps = {
  isLoading: boolean
  mutate: KeyedMutator<UsersResponse[]>
  data:
    | {
        users: User[]
      }
    | undefined
}

type UserRowProps = {
  users: SWRResponse<UsersResponse> | UsersProps
  i: number
  user: User
}
const UserRow = (props: UserRowProps) => {
  const session = useSession()
  const accessToken = session.response?.data.accessToken
  const { showBoundary } = useErrorBoundary()
  return (
    <div>
      <div className="flex items-center gap-4">
        <Link href={`/foodies/${props.user.userName}`}>
          <div className="min-w-[54px]">
            <AppImage
              variant={{ type: 'profile', user: props.user }}
              rounded="full"
              width={50}
              height={50}
              alt={`${props.user.displayName}さんのプロフィール画像`}
              src={userProfileIcon(props.user)}
            />
          </div>
        </Link>
        <div className="min-w-0 flex-1">
          <div className="flex items-center">
            <div className="flex-1">
              <Link href={`/foodies/${props.user.userName}`}>
                <div>
                  <div className="font-bold text-sm flex items-center gap-1">
                    {props.user.displayName}{' '}
                    {props.user.warningStatus != null && (
                      <UserWarningStatusIconButton
                        userWarningStatus={props.user.warningStatus}
                      />
                    )}
                    <span className="text-xs text-[#6C6C6C] font-normal">
                      @{props.user.userName}
                    </span>
                  </div>
                  <div className="text-[13px]">
                    {userAttributesLabel(props.user)}
                  </div>
                </div>
              </Link>
            </div>
            {session.response?.data.userId === props.user.userId ? (
              <div></div>
            ) : props.user.following ? (
              <button
                className="rounded border border-gray py-1 px-4 text-[12px] w-[104px] h-[26px]"
                onClick={async () => {
                  if (accessToken) {
                    try {
                      await userUnfollowUser({
                        accessToken,
                        searchParams: {
                          user_id: props.user.userId,
                        },
                      })
                      await props.users.mutate()
                    } catch (e) {
                      showBoundary(e)
                    }
                  }
                }}
              >
                フォロー中
              </button>
            ) : (
              <button
                className="rounded bg-function-cv2 text-function py-1 px-4 text-[12px] w-[104px] h-[26px]"
                onClick={async () => {
                  if (accessToken) {
                    try {
                      await userFollowUser({
                        accessToken,
                        body: {
                          user_id: props.user.userId,
                        },
                      })
                      await props.users.mutate()
                    } catch (e) {
                      showBoundary(e)
                    }
                  }
                }}
              >
                フォローする
              </button>
            )}
          </div>
          <div className="whitespace-pre-wrap break-words text-[13px]  line-clamp-3">
            {props.user.biography}
          </div>
        </div>
      </div>

      <div
        className={cx(
          'w-full my-5',
          (props.users.data?.users.length ?? 0) - 1 !== props.i &&
            'border-b border-gray'
        )}
      />
    </div>
  )
}

type Props = {}
const Page: NextPage<Props> = () => {
  const [searchQuery, setSearchQuery] = useState('')

  const searchQueryInputRef = useRef<HTMLInputElement | null>(null)

  const onChangeSearchQuery = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value)
    },
    []
  )
  const searchUsers = useFoodSWRInfinite(
    '/user/search_users',
    { key: searchQuery },
    (pageIndex, _) => {
      return {
        count: config.pageSize.userList,
        offset: pageIndex * config.pageSize.userList,
      }
    },
    { disabled: searchQuery === '' }
  )
  const flattenSearchUsers = searchUsers.data?.reduce((acc, resp) => {
    return [...acc, ...resp.users]
  }, [] as User[])
  const recomendedUsers = useUserRecommendedUsers()
  const users = useMemo(
    () =>
      searchQuery
        ? {
            isLoading: searchUsers.isLoading,
            mutate: searchUsers.mutate,
            data: flattenSearchUsers
              ? { users: flattenSearchUsers }
              : undefined,
          }
        : recomendedUsers,
    [
      flattenSearchUsers,
      recomendedUsers,
      searchQuery,
      searchUsers.isLoading,
      searchUsers.mutate,
    ]
  )

  const rookieUsers = useUserRookieUsers()

  return (
    <>
      <Head>
        <title>探す</title>
      </Head>
      <Header>
        <div />
        探す
        <div />
      </Header>

      <Container>
        <ContainerInset>
          <div className="flex flex-col gap-5">
            <ButtonGroup className="w-full">
              <Link href="/search/foodies">
                <Button variant="functionWeak" data-current={true}>
                  フーディーを探す
                </Button>
              </Link>
              <Link href="/search/restaurants">
                <Button variant="functionWeak">店舗を探す</Button>
              </Link>
            </ButtonGroup>

            <TextField
              leftIcon={<SearchIcon className="w-4 h-4" fill="#333" />}
              inputProps={{
                placeholder: '名前、ユーザーIDから検索する',
                ref: searchQueryInputRef,
                onChange: onChangeSearchQuery,
              }}
            />
          </div>
        </ContainerInset>

        {searchQuery === '' && (
          <ContainerInset>
            <div className="mb-4">
              {
                <Heading variant="h3-strong" component="h2">
                  {!rookieUsers.isLoading &&
                    rookieUsers.data?.users &&
                    rookieUsers.data?.users.length > 0 &&
                    '新着のフーディー'}
                </Heading>
              }
            </div>

            {rookieUsers.data?.users.map((user, i) => {
              if (user.muted || user.warningStatus != null) {
                return <Fragment key={i}></Fragment>
              }
              return (
                <UserRow
                  key={user.userId}
                  users={rookieUsers}
                  i={i}
                  user={user}
                />
              )
            })}
          </ContainerInset>
        )}

        <ContainerInset>
          <div className="mt-6 mb-4">
            {
              <Heading variant="h3-strong" component="h2">
                {searchQuery !== '' &&
                !users.isLoading &&
                (users.data?.users.length ?? 0) === 0
                  ? '検索結果'
                  : '注目のフーディー'}
              </Heading>
            }
          </div>
          {(searchQuery !== '' &&
            !users.isLoading &&
            (users.data?.users.length ?? 0)) === 0 && (
            <div>一致する検索結果はありませんでした</div>
          )}
          <InfiniteScroll
            className="flex flex-col mx-auto w-full"
            dataLength={flattenSearchUsers?.length ?? 0}
            next={() => {
              searchUsers.setSize((size) => size + 1)
            }}
            hasMore={
              (searchUsers.data &&
                searchUsers.data[searchUsers.data.length - 1]?.users.length !==
                  0) ??
              false
            }
            loader={<div className="loader" key={0} />}
          >
            {/* ブロック解除などが出来なくなるため、検索結果（= searchQuery !== ''）からは除外しない */}
            {users.data?.users.map((user, i) => {
              if (
                searchQuery === '' &&
                (user.muted || user.warningStatus != null)
              ) {
                return <Fragment key={i}></Fragment>
              }

              return (
                <UserRow key={user.userId} users={users} i={i} user={user} />
              )
            })}
          </InfiniteScroll>
        </ContainerInset>
      </Container>
      <Footer />
    </>
  )
}

export default Page
